import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import WorkbookVideoPlayer from './WorkbookVideoPlayer';
import { checkAndUnlockPostAssessment, WorkbookCompletionModal } from '../utils/checkAndUnlockPostAssessment';

const AI_SCORING_PROMPT = `You are an expert AI literacy coach evaluating a learner's role-specific RICE prompt.

Score each criterion 1-4 (1=Missing, 2=Partial, 3=Solid, 4=Excellent).

ROLE SPECIFICITY: Does it name the function, org type, and expertise level needed? (1-4)

AUDIENCE CLARITY: Does it name the audience and what they need from the output? (1-4)

ORG CONTEXT: Does it describe the org well enough to shape how the output lands? (1-4)

OUTPUT STANDARDS: Does it specify format, tone, length, inclusions, and exclusions? (1-4)

OVERALL QUALITY: Would this produce role-specific output near-ready to use? (1-4)

After scoring:
1. Give a total score out of 20.
2. Identify the weakest role context component and rewrite just that part.
3. Show the full improved prompt.
4. In one sentence: what does the improved version know that the original didn't?

Here is the prompt to evaluate:

[PASTE YOUR FULL ROLE-SPECIFIC RICE PROMPT HERE]`;

const RUBRIC_ROWS = [
  {
    criterion: 'ROLE SPECIFICITY',
    missing: 'No role or generic title only',
    partial: 'Job title without org context',
    solid: 'Function and org type named',
    excellent: 'Function, org type, and expertise level all specified',
  },
  {
    criterion: 'AUDIENCE CLARITY',
    missing: 'No audience named',
    partial: 'Audience named but vague',
    solid: 'Audience named with what they need',
    excellent: 'Audience, their decision context, and stakes all specified',
  },
  {
    criterion: 'ORG CONTEXT',
    missing: 'No org context',
    partial: 'One org detail (e.g. size only)',
    solid: 'Size and industry included',
    excellent: 'Size, industry, pace, and stakes: enough to shape the output',
  },
  {
    criterion: 'OUTPUT STANDARDS',
    missing: 'No format or standards',
    partial: 'One standard (e.g. length only)',
    solid: 'Format and tone specified',
    excellent: 'Format, tone, length, inclusions, and exclusions all addressed',
  },
  {
    criterion: 'OVERALL QUALITY',
    missing: 'Would produce generic output',
    partial: 'Better than generic, still vague',
    solid: 'Clearly role-specific output',
    excellent: 'Output would be near-ready to use with minimal editing',
  },
];

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, minHeight = 60 }) {
  return (
    <div style={s.field}>
      {label && <div style={s.fieldLabel}>{label}</div>}
      <textarea style={{ ...s.textarea, minHeight }} value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function ScoreField({ label, value, onChange }) {
  return (
    <div style={s.scoreField}>
      <div style={s.scoreLabel}>{label}</div>
      <select style={s.scoreSelect} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
      </select>
      <div style={s.scoreOf}>/ 4</div>
    </div>
  );
}

function RubricTable({ rows }) {
  return (
    <div style={s.rubricTable}>
      <div style={s.rubricHeader}>
        {['CRITERION', '1 — MISSING', '2 — PARTIAL', '3 — SOLID', '4 — EXCELLENT'].map(h => (
          <div key={h} style={s.rubricCol0}>{h}</div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={s.rubricRow}>
          <div style={{ ...s.rubricCol0, background: '#F2F0EC', color: '#1B2A4A', fontWeight: '700' }}>{row.criterion}</div>
          <div style={s.rubricCol}>{row.missing}</div>
          <div style={s.rubricCol}>{row.partial}</div>
          <div style={s.rubricCol}>{row.solid}</div>
          <div style={s.rubricCol}>{row.excellent}</div>
        </div>
      ))}
    </div>
  );
}

function CalloutBox({ title, children }) {
  return (
    <div style={s.callout}>
      <div style={s.calloutTitle}>{title}</div>
      <div style={s.calloutBody}>{children}</div>
    </div>
  );
}

function calcTotal(data, keys) {
  return keys.reduce((sum, k) => sum + (parseInt(data[k]) || 0), 0);
}

export default function WorkbookDay4({ user, onNav }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPostUnlockModal, setShowPostUnlockModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      const wbSnap = await getDoc(doc(db, 'workbooks', user.uid));
      if (wbSnap.exists()) {
        const prog = wbSnap.data().progress || {};
        const d = prog['day4'] || {};
        setData(d.sections || {});
        setIsComplete(d.status === 'complete');
      }
    };
    load();
  }, [user.uid]);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day4: { ...(existing.progress?.day4 || {}), sections: data, updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setSaving(false);
    setLastSaved(new Date());
  };

  const handleMarkComplete = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day4: { ...(existing.progress?.day4 || {}), sections: data, status: 'complete', completedAt: serverTimestamp(), updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setIsComplete(true);
    setShowCompleteModal(false);
    setSaving(false);
    // Check if all assigned workbooks are now complete, unlock post-assessment if so
    const unlockResult = await checkAndUnlockPostAssessment(user.uid);
    if (unlockResult.unlocked) {
      setShowPostUnlockModal(true);
    }
  };

  const handleUnmarkComplete = async () => {
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      progress: { ...(existing.progress || {}), day4: { ...(existing.progress?.day4 || {}), status: 'incomplete', updatedAt: serverTimestamp() } }
    }, { merge: true });
    setIsComplete(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(AI_SCORING_PROMPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const rubricTotal = calcTotal(data, ['score_role', 'score_audience', 'score_org', 'score_standards', 'score_overall']);

  return (
    <div style={s.wrapper}>
      <div style={s.topBar}>
        <span style={s.backLink} onClick={() => onNav('workbooks')}>← Back to Workbooks</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isComplete && <div style={s.completeBadge}>✓ Day 4 Complete</div>}
          {lastSaved && <span style={s.savedNote}>Saved {lastSaved.toLocaleTimeString()}</span>}
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
        </div>
      </div>

      <WorkbookVideoPlayer dayNumber={4} user={user} />

      <div style={s.header}>
        <div style={s.dayTag}>Day 4</div>
        <div style={s.headerTitle}>Role-Specific Application</div>
        <div style={s.headerDesc}>Stop writing prompts for everyone. Start writing prompts for you.</div>
      </div>

      <div style={s.keyConceptBox}>
        <div style={s.keyConceptLabel}>Key Concept Reminder</div>
        <div style={s.keyConceptText}>
          Generic prompts get generic results, every time. The fix is role context: the four pieces that make a prompt specific to you. Your function tells AI the discipline and vocabulary. Your audience tells it who needs to act on the output and what they care about. Your organizational context tells it what your world looks like: size, industry, pace, stakes. Your output standards tell it what good looks like in your organization. Role context doesn't replace RICE. It lives inside it, specifically in the Role and Context components. When all four pieces are in the prompt, AI stops producing something that could work for anyone and starts producing something that works for you.
        </div>
      </div>

      <Section title="In-Session Exercise — Build Your Role-Specific Prompt">
        <div style={s.exerciseIntro}>
          Use your top use case from Day 3: the one you said you would run every single day. Your output description from Day 3 becomes the Intent component here. Build the full prompt with all four role context pieces baked in.
        </div>

        <div style={s.partHeader}>STEP 1 — LOCK YOUR ROLE CONTEXT</div>

        <div style={s.rcTable}>
          {[
            {
              key: 'rc_function',
              name: 'MY FUNCTION',
              hint: 'What department or discipline do you work in? Be specific. Example: HR Business Partner / B2B Sales / Finance Business Partner / Marketing Manager',
            },
            {
              key: 'rc_audience',
              name: 'MY AUDIENCE',
              hint: 'Who reads or acts on this output? What do they need from it? Example: CHRO who needs to make a people risk decision before the leadership meeting',
            },
            {
              key: 'rc_org',
              name: 'MY ORG CONTEXT',
              hint: 'What does your organization look like? Size, industry, pace, stakes. Example: 800-person financial services company, fast-paced, highly regulated, formal communication norms',
            },
            {
              key: 'rc_standards',
              name: 'MY OUTPUT STANDARDS',
              hint: 'What does good look like in your world? Format, tone, length, what to always include, what to never include. Example: No bullet points, under 250 words, professional but not stiff, always include action items with owners',
            },
          ].map((row, i, arr) => (
            <div key={row.key} style={{ ...s.rcRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.rcLabel}>
                <div style={s.rcLabelName}>{row.name}</div>
                <div style={s.rcLabelHint}>{row.hint}</div>
              </div>
              <textarea
                style={{ ...s.textarea, flex: 1, border: 'none', minHeight: 66, background: '#FAF8F5' }}
                value={data[row.key] || ''}
                onChange={e => set(row.key, e.target.value)}
              />
            </div>
          ))}
        </div>

        <div style={s.partHeader}>STEP 2 — BUILD THE FULL RICE PROMPT</div>

        <Field label="My top use case (from Day 3):" value={data.top_use_case} onChange={v => set('top_use_case', v)} minHeight={44} />
        <Field label="My output description (from Day 3). This is your Intent:" value={data.output_description} onChange={v => set('output_description', v)} minHeight={44} />

        <div style={s.riceTable}>
          {[
            { letter: 'R', key: 'rice_r', hint: 'Who should AI be? Use your function, org type, and the expertise level this task requires.' },
            { letter: 'I', key: 'rice_i', hint: 'Your output description from above goes here. Exact and specific.' },
            { letter: 'C', key: 'rice_c', hint: 'Your audience + org context. Who reads it, what they need, what your world looks like.' },
            { letter: 'E', key: 'rice_e', hint: 'Your output standards. Format, tone, length, what to include, what to leave out.' },
          ].map((row, i, arr) => (
            <div key={row.key} style={{ ...s.riceRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.riceTag}><div style={s.riceTagText}>{row.letter}</div></div>
              <div style={s.riceHint}>{row.hint}</div>
              <textarea
                style={{ ...s.textarea, flex: 1, border: 'none', minHeight: 66, background: '#FAF8F5' }}
                value={data[row.key] || ''}
                onChange={e => set(row.key, e.target.value)}
              />
            </div>
          ))}
        </div>

        <Field label="Your complete role-specific RICE prompt (write the full combined version here):" value={data.full_prompt} onChange={v => set('full_prompt', v)} minHeight={90} />
        <Field label="What came back: was it noticeably different from a generic prompt?" value={data.came_back} onChange={v => set('came_back', v)} minHeight={70} />
      </Section>

      <Section title="Scoring Criteria">
        <div style={{ fontSize: '13px', color: '#3D3D3D', marginBottom: '16px', lineHeight: '1.6' }}>
          Use these criteria to evaluate your role-specific RICE prompt.
        </div>
        <RubricTable rows={RUBRIC_ROWS} />
        <div style={{ fontSize: '13px', fontWeight: '500', color: '#1B2A4A', margin: '16px 0 12px' }}>Score your prompt:</div>
        <div style={s.scoreRow}>
          <ScoreField label="Role" value={data.score_role} onChange={v => set('score_role', v)} />
          <ScoreField label="Audience" value={data.score_audience} onChange={v => set('score_audience', v)} />
          <ScoreField label="Org" value={data.score_org} onChange={v => set('score_org', v)} />
          <ScoreField label="Standards" value={data.score_standards} onChange={v => set('score_standards', v)} />
          <ScoreField label="Overall" value={data.score_overall} onChange={v => set('score_overall', v)} />
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>Total</div>
            <div style={s.scoreTotalVal}>{rubricTotal} / 20</div>
          </div>
        </div>
      </Section>

      <div style={s.aiBox}>
        <div style={s.aiBoxTitle}>AI Scoring Prompt — Evaluate Your Own Work</div>
        <div style={s.aiBoxSub}>Paste this prompt into your AI tool along with your complete role-specific RICE prompt.</div>
        <div style={s.aiBoxText}>{AI_SCORING_PROMPT}</div>
        <button style={s.copyBtn} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy AI Scoring Prompt'}</button>
      </div>

      <Section title="Scores from AI Evaluation">
        <Field label="AI Score: ___ / 20     Weakest component identified:" value={data.ai_score} onChange={v => set('ai_score', v)} minHeight={44} />
        <Field label="What did the improved version know that the original didn't?" value={data.ai_improved} onChange={v => set('ai_improved', v)} minHeight={70} />
      </Section>

      <CalloutBox title="THE STANDARD: 16 / 20">
        Keep working until your role-specific prompt scores 16/20 or higher AND the output it produces is noticeably different, and better, than what a generic prompt would give you. Use the revision workspace below.
      </CalloutBox>

      <Section title="Revision Workspace — Keep Going Until You Hit the Standard">
        <div style={s.revisionLabel}>REVISION 1</div>
        <Field label="What I changed and why:" value={data.rev1_what} onChange={v => set('rev1_what', v)} />
        <Field label="Revised prompt:" value={data.rev1_prompt} onChange={v => set('rev1_prompt', v)} minHeight={80} />
        <Field label="New AI Score: ___ / 20" value={data.rev1_score} onChange={v => set('rev1_score', v)} minHeight={44} />
        <div style={{ ...s.revisionLabel, marginTop: '24px' }}>REVISION 2 (if needed)</div>
        <Field label="What I changed and why:" value={data.rev2_what} onChange={v => set('rev2_what', v)} />
        <Field label="Revised prompt:" value={data.rev2_prompt} onChange={v => set('rev2_prompt', v)} minHeight={80} />
        <Field label="New AI Score: ___ / 20" value={data.rev2_score} onChange={v => set('rev2_score', v)} minHeight={44} />
      </Section>

      <Section title="Prompt Library Entry — Log It Before You Close This Book">
        <div style={{ fontSize: '13px', color: '#3D3D3D', marginBottom: '16px', lineHeight: '1.6' }}>
          Fill in all six fields right now. A prompt you built but didn't log is a prompt you rebuild from scratch.
        </div>
        <div style={s.libBlock}>
          <div style={s.libTitle}>My Prompt Library Entry</div>
          {[
            { key: 'lib_usecase', name: '01 USE CASE', hint: 'What specific task does this prompt handle?' },
            { key: 'lib_workflow', name: '02 WORKFLOW', hint: 'Which step in which workflow?' },
            { key: 'lib_roletag', name: '03 ROLE TAG', hint: 'Your function + org type + expertise level needed.' },
            { key: 'lib_prompt', name: '04 THE PROMPT', hint: 'Full prompt text. Every bracket clearly marked.', tall: true },
            { key: 'lib_output', name: '05 OUTPUT NOTE', hint: 'One sentence: what does AI produce and where does it go?' },
            { key: 'lib_chain', name: '06 CHAIN PROMPT', hint: 'The next logical step you would take with that output.' },
          ].map((row, i, arr) => (
            <div key={row.key} style={{ ...s.libRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.libLabel}>
                <div style={s.libLabelName}>{row.name}</div>
                <div style={s.libLabelHint}>{row.hint}</div>
              </div>
              <textarea
                style={{ ...s.textarea, flex: 1, border: 'none', minHeight: row.tall ? 80 : 50, background: '#FAF8F5' }}
                value={data[row.key] || ''}
                onChange={e => set(row.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Reflection">
        <Field
          label="1. Compare the output from your role-specific prompt to what a generic version of the same task would produce. What specifically is different, and why does that difference matter for your actual work?"
          value={data.reflection_1}
          onChange={v => set('reflection_1', v)}
          minHeight={80}
        />
        <Field
          label="2. Which of the four role context pieces was hardest to fill in, and what does that tell you about how you've been thinking about prompting up to now?"
          value={data.reflection_2}
          onChange={v => set('reflection_2', v)}
          minHeight={80}
        />
      </Section>

      <Section title="Wins Tracker — Day 4">
        <div style={s.winsTable}>
          <div style={s.winsHeader}>
            {['What I Tried', 'Tool Used', 'What Changed', 'Time Saved'].map(h => (
              <div key={h} style={s.winsCol}>{h}</div>
            ))}
          </div>
          {[1, 2, 3].map(n => (
            <div key={n} style={s.winsRow}>
              {['tried', 'tool', 'changed', 'saved'].map(f => (
                <div key={f} style={s.winsCell}>
                  <textarea style={s.winsInput} value={data[`wins_${n}_${f}`] || ''} onChange={e => set(`wins_${n}_${f}`, e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      <CalloutBox title="BEFORE DAY 5 — YOUR ASSIGNMENT">
        <span>Build role-specific RICE prompts for two more use cases from your Day 3 list. For each one:</span>
        <ol style={{ paddingLeft: '18px', marginTop: '8px', lineHeight: '1.8' }}>
          <li>Write the full RICE prompt with all four role context pieces: function, audience, org context, and output standards.</li>
          <li>Run it and note what came back: how does the output compare to what a generic prompt would produce?</li>
          <li>Run the AI scoring prompt. Keep revising until you hit 16/20.</li>
          <li>Log all three prompts in your Prompt Library with all six fields complete including chain prompts.</li>
          <li>Bring your library to Day 5. Note the total time each workflow step used to take versus how long it takes now.</li>
        </ol>
      </CalloutBox>
      <Field label="My two additional use cases for before Day 5:" value={data.assignment_cases} onChange={v => set('assignment_cases', v)} minHeight={66} />

      <div style={s.bottomBar}>
        <div style={s.saveRow}>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Progress'}</button>
          {lastSaved && <span style={s.savedNote}>Last saved {lastSaved.toLocaleTimeString()}</span>}
        </div>
        {isComplete ? (
          <div style={s.completeRow}>
            <div style={s.completedBadge}>✓ Day 4 Complete</div>
            <button style={s.unmarkBtn} onClick={handleUnmarkComplete}>Unmark as Complete</button>
          </div>
        ) : (
          <button style={s.completeBtn} onClick={() => setShowCompleteModal(true)}>Mark Day 4 as Complete</button>
        )}
      </div>

      {showCompleteModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Mark Day 4 as Complete?</div>
            <div style={s.modalText}>This will count toward your certificate progress. You can unmark it if you need to come back.</div>
            <div style={s.modalBtns}>
              <button style={s.modalCancel} onClick={() => setShowCompleteModal(false)}>Cancel</button>
              <button style={s.modalConfirm} onClick={handleMarkComplete}>Yes, Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      <WorkbookCompletionModal
        isOpen={showPostUnlockModal}
        onClose={() => setShowPostUnlockModal(false)}
        onGoToAssessment={() => { setShowPostUnlockModal(false); onNav('assessment'); }}
        onGoToExam={() => { setShowPostUnlockModal(false); onNav('exam'); }}
      />
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '720px', paddingBottom: '60px' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '12px 20px', marginBottom: '16px', position: 'sticky', top: '52px', zIndex: 50 },
  backLink: { fontSize: '12px', color: '#1B2A4A', cursor: 'pointer', fontWeight: '500' },
  header: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '20px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  headerTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff', marginBottom: '6px' },
  headerDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '300' },
  completeBadge: { display: 'inline-block', background: '#1a8a4a', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 12px' },
  keyConceptBox: { background: '#F2F0EC', borderLeft: '3px solid #D4A847', padding: '16px 20px', marginBottom: '20px' },
  keyConceptLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  keyConceptText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.8' },
  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '16px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #E8E6E2' },
  exerciseIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '20px' },
  partHeader: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A', background: '#F2F0EC', padding: '10px 14px', marginBottom: '12px', borderLeft: '3px solid #D4A847' },
  field: { marginBottom: '16px' },
  fieldLabel: { fontSize: '13px', fontWeight: '500', color: '#1B2A4A', marginBottom: '6px', lineHeight: '1.5' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box' },
  rcTable: { border: '1.5px solid #E8E6E2', marginBottom: '20px' },
  rcRow: { display: 'flex' },
  rcLabel: { width: '160px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2' },
  rcLabelName: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  rcLabelHint: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.5' },
  riceTable: { border: '1.5px solid #E8E6E2', marginBottom: '16px' },
  riceRow: { display: 'flex' },
  riceTag: { width: '52px', flexShrink: 0, background: '#1B2A4A', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '14px', borderRight: '1px solid #E8E6E2' },
  riceTagText: { fontSize: '13px', fontWeight: '700', color: '#D4A847', letterSpacing: '1px' },
  riceHint: { width: '170px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2', fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.5' },
  rubricTable: { border: '1.5px solid #E8E6E2', fontSize: '12px', marginBottom: '8px' },
  rubricHeader: { display: 'grid', gridTemplateColumns: '155px 1fr 1fr 1fr 1fr', background: '#1B2A4A', color: '#FFFFFF', fontWeight: '700', fontSize: '11px' },
  rubricRow: { display: 'grid', gridTemplateColumns: '155px 1fr 1fr 1fr 1fr', borderBottom: '1px solid #E8E6E2' },
  rubricCol0: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', fontSize: '11px' },
  rubricCol: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', color: '#3D3D3D', lineHeight: '1.5', fontSize: '11px' },
  scoreRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' },
  scoreField: { display: 'flex', alignItems: 'center', gap: '6px' },
  scoreLabel: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A' },
  scoreSelect: { border: '1.5px solid #E8E6E2', padding: '6px 8px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', background: '#FAF8F5', outline: 'none', width: '56px' },
  scoreOf: { fontSize: '12px', color: '#3D3D3D' },
  scoreTotalBox: { marginLeft: 'auto', background: '#1B2A4A', padding: '8px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  scoreTotalLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' },
  scoreTotalVal: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '900', color: '#D4A847' },
  aiBox: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  aiBoxTitle: { fontSize: '13px', fontWeight: '600', color: '#D4A847', marginBottom: '6px' },
  aiBoxSub: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '300', marginBottom: '12px' },
  aiBoxText: { fontSize: '12px', color: '#FFFFFF', lineHeight: '1.8', marginBottom: '12px', fontWeight: '300', whiteSpace: 'pre-line' },
  copyBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  revisionLabel: { fontSize: '12px', fontWeight: '700', color: '#1B2A4A', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '12px' },
  callout: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  calloutTitle: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  calloutBody: { fontSize: '13px', color: '#FFFFFF', fontWeight: '300', lineHeight: '1.7' },
  libBlock: { border: '1.5px solid #E8E6E2' },
  libTitle: { background: '#1B2A4A', color: '#D4A847', padding: '8px 16px', fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' },
  libRow: { display: 'flex' },
  libLabel: { width: '155px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2' },
  libLabelName: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  libLabelHint: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.5' },
  winsTable: { border: '1.5px solid #E8E6E2' },
  winsHeader: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#1B2A4A' },
  winsCol: { padding: '10px 12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  winsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: '1px solid #E8E6E2' },
  winsCell: { borderRight: '1px solid #E8E6E2', padding: '4px' },
  winsInput: { width: '100%', border: 'none', padding: '8px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '50px' },
  bottomBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  savedNote: { fontSize: '11px', color: '#1a8a4a', fontWeight: '500' },
  completeRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  completedBadge: { background: '#eaf3de', color: '#3b6d11', padding: '8px 16px', fontSize: '12px', fontWeight: '700' },
  unmarkBtn: { fontSize: '12px', color: '#3D3D3D', background: 'none', border: '1px solid #E8E6E2', padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  completeBtn: { background: '#C0392B', color: '#fff', border: 'none', padding: '12px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(27,42,74,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modal: { background: '#FAF8F5', padding: '36px 40px', maxWidth: '420px', width: '90%' },
  modalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  modalText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px' },
  modalCancel: { background: 'transparent', color: '#3D3D3D', border: '1.5px solid #E8E6E2', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  modalConfirm: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
